// frontend/src/pages/admin/EmailGroupForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Switch, Typography, Table, Popconfirm, Select, Space, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

const EmailGroupForm = ({ visible, onCancel, onSuccess, emailGroup }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [userToAssign, setUserToAssign] = useState(null);
  const [assigningLoading, setAssigningLoading] = useState(false);
  
  const notificationApi = useNotificationApi();

  // Standardized notification functions
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();
    
    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Fetch all available users
  useEffect(() => {
    if (visible && allUsers.length === 0) {
      const fetchUsers = async () => {
        const loadingKey = 'fetchUsersLoading';
        const hideLoading = showLoading(loadingKey, 'Loading users...');
        
        try {
          const response = await api.get('/api/users?status=active');
          setAllUsers(response.data.data || []);
        } catch (error) {
          console.error('Error fetching users:', error.response?.data || error.message);
          showError('Failed to Load Users', error.response?.data?.message || 'Failed to load users.', 'fetchUsersError');
        } finally {
          hideLoading();
        }
      };
      fetchUsers();
    }
  }, [visible, allUsers.length, showLoading, showError]);

  // Refetch assigned members
  const refetchAssignedMembers = useCallback(async () => {
    if (!emailGroup?.id) return;

    const loadingKey = 'refetchAssignedMembersLoading';
    const hideLoading = showLoading(loadingKey, 'Loading group members...');
    setAssigningLoading(true);

    try {
      const response = await api.get(`/api/email-groups/${emailGroup.id}/members`);
      const fetchedMembers = response.data.data || [];
      
      setAssignedMembers(fetchedMembers);
    } catch (error) {
      console.error('Error refetching assigned members:', error.response?.data || error.message);
      showError('Failed to Refresh Data', error.response?.data?.message || 'Failed to refresh group members.', 'refetchAssignedMembersError');
    } finally {
      setAssigningLoading(false);
      hideLoading();
    }
  }, [emailGroup, showLoading, showError]);

  // Primary useEffect for form initialization
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setAssignedMembers([]);
      setUserToAssign(null);
      return;
    }
    
    form.resetFields();

    if (emailGroup) {
      const formValues = {
        name: emailGroup.name,
        description: emailGroup.description,
        groupEmailAddress: emailGroup.groupEmailAddress,
        isActive: Boolean(emailGroup.isActive)
      };

      form.setFieldsValue(formValues);

      if (emailGroup.id) {
        refetchAssignedMembers();
      }
    } else {
      form.resetFields();
      setAssignedMembers([]);
      setUserToAssign(null);
      form.setFieldsValue({
        isActive: true
      });
    }
  }, [visible, emailGroup, form, refetchAssignedMembers]);

  // Handle form submission
const handleSubmit = async () => {
  let hideLoading;
  try {
    setLoading(true);
    hideLoading = showLoading('emailGroupSubmitLoading', 
      emailGroup ? 'Updating email group...' : 'Creating email group...');

    // Validate form fields first
    const values = await form.validateFields();
    
    // Prepare payload with camelCase keys (backend will convert to snake_case)
    const payload = {
      name: values.name.trim(), // Trim whitespace
      description: values.description?.trim(), // Optional field
      groupEmailAddress: values.groupEmailAddress?.trim(), // Optional field
      isActive: Boolean(values.isActive) // Ensure boolean value
    };

    console.debug('Submitting payload:', payload); // Debug log

    // Make API request
    const response = emailGroup 
      ? await api.put(`/api/email-groups/${emailGroup.id}`, payload)
      : await api.post('/api/email-groups', payload);

    console.debug('API response:', response.data); // Debug log

    // Show success message
    showSuccess(
      emailGroup ? 'Email Group Updated' : 'Email Group Created',
      emailGroup 
        ? 'Email group updated successfully!' 
        : 'New email group created successfully!',
      'emailGroupFormSuccess'
    );

    // Close modal after delay
    setTimeout(() => {
      if (hideLoading) hideLoading();
      onSuccess(); // Refresh parent component
      onCancel(); // Close modal
    }, 500);

  } catch (error) {
    if (hideLoading) hideLoading();
    
    // Enhanced error logging
    console.error('Submission error details:', {
      error: error,
      response: error.response,
      config: error.config,
      request: error.request
    });

    // Extract error message from different possible locations
    const errorMessage = (
      error.response?.data?.message ||
      error.response?.data?.error ||
      (error.response?.data?.errors && Object.values(error.response.data.errors).join(', ')) ||
      error.message ||
      'Failed to save email group. Please try again.'
    );

    showError('Submission Failed', errorMessage, 'emailGroupFormSubmitError');

    // Handle specific error cases
    if (error.response?.status === 409) {
      // Handle conflict (duplicate name or email)
      form.setFields([
        {
          name: error.response.data.field === 'name' ? 'name' : 'groupEmailAddress',
          errors: [errorMessage]
        }
      ]);
    }
  } finally {
    setLoading(false);
  }
};

  // Member management functions
  const handleAssignMember = async () => {
    if (!userToAssign || !emailGroup?.id) return;

    const loadingKey = 'assignMemberLoading';
    const hideLoading = showLoading(loadingKey, 'Assigning member...');
    setAssigningLoading(true);

    try {
      await api.post(`/api/email-groups/${emailGroup.id}/members`, { userId: userToAssign });
      showSuccess('Member Assigned', 'Member assigned successfully!', 'assignMemberSuccess');
      setUserToAssign(null);
      await refetchAssignedMembers();
    } catch (error) {
      console.error('Error assigning member:', error.response?.data || error.message);
      const errorMsg = error.response?.status === 409 
        ? 'This user is already a member of this group.'
        : error.response?.data?.message || 'Failed to assign member.';
      showError('Assignment Failed', errorMsg, 'assignMemberError');
    } finally {
      setAssigningLoading(false);
      hideLoading();
    }
  };

  const handleRemoveAssignedMember = async (memberId) => {
    if (!emailGroup?.id) return;

    const loadingKey = 'removeMemberLoading';
    const hideLoading = showLoading(loadingKey, 'Removing member...');
    setAssigningLoading(true);

    try {
      await api.delete(`/api/email-groups/${emailGroup.id}/members/${memberId}`);
      showSuccess('Member Removed', 'Member removed successfully!', 'removeMemberSuccess');
      await refetchAssignedMembers();
    } catch (error) {
      console.error('Error removing member:', error.response?.data || error.message);
      showError('Removal Failed', error.response?.data?.message || 'Failed to remove member.', 'removeMemberError');
    } finally {
      setAssigningLoading(false);
      hideLoading();
    }
  };

  const assignedMembersColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      sorter: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || '')
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      sorter: (a, b) => (a.userType || '').localeCompare(b.userType || '')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={`Are you sure you want to remove ${record.firstName} ${record.lastName}?`}
          onConfirm={() => handleRemoveAssignedMember(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />} aria-label={`Remove ${record.firstName} ${record.lastName}`} />
        </Popconfirm>
      ),
    },
  ];

  const availableUsersForAssignment = allUsers.filter(
    (user) => !assignedMembers.some((member) => member.userId === user.id)
  );

  return (
    <Modal
      open={visible}
      title={emailGroup ? 'Edit Email Group' : 'Add New Email Group'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={800}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        name="email_group_form"
        initialValues={{
          isActive: true
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Group Name"
                rules={[
                  { required: true, message: 'Please input the group name!' },
                  { type: 'string', min: 2, message: 'Group name must be at least 2 characters.' }
                ]}
              >
                <Input placeholder="e.g., Support Team" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="groupEmailAddress"
                label="Group Email Address"
                rules={[
                  { required: true, message: 'Please input the group email address!' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input placeholder="e.g., support@company.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Brief description of the email group" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          {emailGroup && (
            <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
              <h3>Manage Members</h3>
              <Row gutter={16} align="middle" style={{ marginBottom: '15px' }}>
                <Col span={16}>
                  <Select
                    placeholder="Select user to add"
                    style={{ width: '100%' }}
                    value={userToAssign}
                    onChange={setUserToAssign}
                    loading={assigningLoading}
                    disabled={assigningLoading || availableUsersForAssignment.length === 0}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {availableUsersForAssignment.map(user => (
                      <Option key={user.id} value={user.id}>
                        {`${user.firstName} ${user.lastName} (${user.email})`}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAssignMember}
                    loading={assigningLoading}
                    disabled={!userToAssign || assigningLoading}
                    block
                  >
                    Add Member
                  </Button>
                </Col>
              </Row>

              <Table
                columns={assignedMembersColumns}
                dataSource={assignedMembers}
                rowKey="id"
                pagination={false}
                loading={assigningLoading}
                size="small"
                scroll={{ y: 240 }}
                locale={{ emptyText: 'No members assigned yet.' }}
              />
            </div>
          )}
        </Space>
      </Form>
    </Modal>
  );
};

export default EmailGroupForm;